import { getCountries, getCountryCallingCode, type CountryCode } from 'libphonenumber-js';

export type { CountryCode };

export interface CountryOption {
  code: CountryCode;
  name: string;
  callingCode: string;
  flag: string;
}

/** ISO 3166-1 alpha-2 code to regional-indicator emoji flag (e.g. "NZ" -> 🇳🇿). */
export function flagEmoji(code: string): string {
  return code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1a5 + c.charCodeAt(0)));
}

let displayNames: Intl.DisplayNames | null | undefined;

/** Localized country name for an ISO code, falling back to the code itself. */
export function countryName(code: string): string {
  if (displayNames === undefined) {
    try {
      displayNames = new Intl.DisplayNames(undefined, { type: 'region' });
    } catch {
      displayNames = null;
    }
  }
  try {
    return displayNames?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

let cachedOptions: CountryOption[] | null = null;

/** Every country libphonenumber knows about, sorted by localized name. */
export function getCountryOptions(): CountryOption[] {
  if (cachedOptions) return cachedOptions;
  cachedOptions = getCountries()
    .map((code) => ({
      code,
      name: countryName(code),
      callingCode: getCountryCallingCode(code),
      flag: flagEmoji(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return cachedOptions;
}

export function isSupportedCountry(code: string | null | undefined): code is CountryCode {
  if (!code) return false;
  return (getCountries() as string[]).includes(code.toUpperCase());
}
